import { Component, OnDestroy, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormControl, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, combineLatest } from "rxjs";
import { ApplicationError } from "../_errors/application-error";
import { InspectionReport, InspectionReportUpdate } from "../_objects/inspection-report";
import { ReportTemplateListItem, ReportTitleFormat } from "../_offline/models/report-preview/report-template";
import { InspectionReportService } from "../_services/http-api/inspection-report.service";
import { ReportTemplateService } from "../_services/http-api/report-template.service";
import { CommonSnackBarService } from "../_services/common-snack-bar.service";
import { UtilsService } from "../_services/_utils.service";
import { REGULAR_EXPRESSION, TenantConfig } from "../app.constants";
import { CustomValidators } from "../_shared/custom-validator";
import { TenantConfigService } from "../_services/http-api/tenant-config.service";
import { SpinnerService } from "../_services/spinner.service";
import { NetworkStatusService } from "./../_services/network-status.service";

export class ReportTemplateWrapper {
  reportTemplate: ReportTemplateListItem;
  removed: boolean;
  constructor(reportTemplate: ReportTemplateListItem) {
    this.reportTemplate = reportTemplate;
  }
}

@Component({
  selector: "app-dialog-report-properties",
  templateUrl: "./inspection-report-edit-properties-dialog.component.html",
  styleUrls: ["./inspection-report-edit-properties-dialog.component.scss"],
  standalone: false
})
export class ReportPropertiesDialogComponent implements OnDestroy {
  templatesMaster: ReportTemplateWrapper[] = [];
  selectedTemplates: ReportTemplateWrapper;

  reportTemplate: ReportTemplateListItem;

  reportTitleMaxLength = 100; // 入力文字数上限
  regularExpression = this.utilsService.CombiningRegExp([
    "^",
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    "[",
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.KANJI_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    "]+$"
  ]);
  reportTitleForm = new FormControl<string>("", [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.reportTitleMaxLength),
    Validators.pattern(this.regularExpression)
  ]);
  reportTemplateForm = new FormControl<ReportTemplateWrapper>(null, [CustomValidators.requiredTrimmed()]);

  inspectionReport: InspectionReport;

  draftReportQualityForm = new FormControl<number>(null, []);
  finalReportQualityForm = new FormControl<number>(null, []);

  reportQualities = [
    {
      displayLabel: "settingTenant.value_high",
      value: 0
    },
    {
      displayLabel: "settingTenant.value_medium",
      value: 1
    },
    {
      displayLabel: "settingTenant.value_low",
      value: 2
    }
  ];

  private onDestroy$ = new Subject();

  get isDesktopAndOffline() {
    return this.networkStatusService.isDesktopAndOffline;
  }

  constructor(
    private inspectionReportService: InspectionReportService,
    private reportTemplateService: ReportTemplateService,
    private oscSpinnerService: SpinnerService,
    private dialogRef: MatDialogRef<ReportPropertiesDialogComponent>,
    private snackBar: CommonSnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService,
    private tenantConfigService: TenantConfigService,
    private utilsService: UtilsService,
    private networkStatusService: NetworkStatusService
  ) {
    // template subjectmodel
    combineLatest([
      this.getReportTemplatesBySubjectModelId(data.subjectModelId),
      this.inspectionReportService.getInspectionReport(data.inspectionReport.reportId)
    ]).subscribe(
      ([item, inspectionReport]) => {
        this.templatesMaster = item.map((templateItem) => this.convertTemplateList2TemplateWrapper(templateItem));
        this.inspectionReport = inspectionReport;

        this.reportTitleForm.setValue(this.inspectionReport.title);

        this.addReportTemplateIfNeeds(this.templatesMaster, this.inspectionReport);
        this.setConfig();
      },
      (error) => {
        console.log(error);
        const faileTeplateSelect = this.translate.instant("inspectionReportEditPropertiesDialog.FailTemplateSelect");
        this.snackBar.open(faileTeplateSelect);
        throw new ApplicationError(faileTeplateSelect);
      }
    );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // TenantConfig取得
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;

    if (configs) {
      const draft = configs.find((_) => _.key === TenantConfig.DefaultImageQualityOfDraftReports.KEY);
      const final = configs.find((_) => _.key === TenantConfig.DefaultImageQualityOfFinalReports.KEY);

      const draftValue =
        this.inspectionReport.draftQuality !== null ? this.inspectionReport.draftQuality : draft.selectItems[draft.value as string].value;
      const finalValue =
        this.inspectionReport.finalQuality !== null ? this.inspectionReport.finalQuality : final.selectItems[final.value as string].value;

      this.draftReportQualityForm.setValue(draftValue);
      this.finalReportQualityForm.setValue(finalValue);
    }
  }

  convertInspectionReport2TemplateWrapper(report: InspectionReport, lists: ReportTemplateWrapper[]): ReportTemplateWrapper {
    if (!report || !report.templateId) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidRpt"));
    }
    const template = new ReportTemplateWrapper(new ReportTemplateListItem());
    lists.forEach((list) => {
      template.reportTemplate.id = report.templateId;
      template.reportTemplate.name = list.reportTemplate.name;
      template.reportTemplate.reportTitleFormat = list.reportTemplate.reportTitleFormat;
      template.reportTemplate.reportTitleInputMode = list.reportTemplate.reportTitleInputMode;
      template.removed = list.removed;
    });
    return template;
  }

  convertTemplateList2TemplateWrapper(item: ReportTemplateListItem): ReportTemplateWrapper {
    if (!item || !item.id) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidRptTemplate"));
    }
    const template = new ReportTemplateWrapper(item);
    template.reportTemplate.id = item.id;
    template.reportTemplate.name = item.name;
    template.reportTemplate.reportTitleFormat = item.reportTitleFormat;
    template.reportTemplate.reportTitleInputMode = item.reportTitleInputMode;
    template.removed = false;
    return template;
  }

  addReportTemplateIfNeeds(reportTemplateList: ReportTemplateWrapper[], inspectionReport: InspectionReport) {
    if (!reportTemplateList || reportTemplateList.length === 0) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidRptTemplate"));
    }

    if (
      !inspectionReport ||
      inspectionReport === null ||
      inspectionReport === undefined ||
      inspectionReport.templateId === null ||
      inspectionReport.templateId === undefined
    ) {
      throw new ApplicationError("Report is invalid.");
    }

    const exist = reportTemplateList.some((data) => {
      return data.reportTemplate.id === inspectionReport.templateId;
    });
    if (!exist) {
      this.reportTemplateService.getReportTemplate(inspectionReport.templateId).subscribe(
        (template) => {
          const tempTemplate = new ReportTemplateWrapper(new ReportTemplateListItem());
          tempTemplate.reportTemplate.id = template.id;
          tempTemplate.reportTemplate.name = template.name;
          tempTemplate.reportTemplate.reportTitleFormat = template.reportTitleFormat;
          tempTemplate.reportTemplate.reportTitleInputMode = template.reportTitleInputMode;
          tempTemplate.removed = true;
          this.templatesMaster.push(tempTemplate);
        },
        (error) => {
          console.log(error);
        },
        () => {
          if (this.inspectionReport.templateId) {
            this.reportTemplateForm.setValue(
              this.templatesMaster.filter((wrapper) => wrapper.reportTemplate.id === this.inspectionReport.templateId)[0]
            );
            this.loadTemplateId();
          }
        }
      );
    } else {
      if (this.inspectionReport.templateId) {
        this.reportTemplateForm.setValue(this.templatesMaster.filter((wrapper) => wrapper.reportTemplate.id === this.inspectionReport.templateId)[0]);
        this.loadTemplateId();
      }
    }
  }

  appendReportTitleFormat(reportTitleFormat: ReportTitleFormat[]): string {
    let titleFormat = "";
    reportTitleFormat.forEach((item) => {
      titleFormat += item.value;
    });
    return titleFormat;
  }

  loadTemplateId() {
    if (this.reportTemplateForm.value) {
      this.templatesMaster.forEach((template) => {
        if (template.reportTemplate.id === this.reportTemplateForm.value.reportTemplate.id) {
          this.reportTemplate = template.reportTemplate;
          this.reportTitleForm.setValue(this.inspectionReport.title);
          this.reportTitleForm.enable();
        }
      });
    }
  }

  getReportTemplatesBySubjectModelId(subjectModelId: string): Observable<ReportTemplateListItem[]> {
    if (!subjectModelId || subjectModelId === null || subjectModelId === undefined) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrNullSubjectModel"));
    }
    return this.reportTemplateService.getReportTemplates(subjectModelId);
  }

  submit(): void {
    if (!this.reportTemplateForm.value || this.reportTemplateForm.value === null || this.reportTemplateForm.value === undefined) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidInspectionRpt"));
    }

    this.oscSpinnerService.show(true);
    const tmpInspectionReport: InspectionReport = this.inspectionReport;
    tmpInspectionReport.templateId = this.reportTemplateForm.value.reportTemplate.id;
    tmpInspectionReport.draftQuality = this.draftReportQualityForm.value;
    tmpInspectionReport.finalQuality = this.finalReportQualityForm.value;
    tmpInspectionReport.title = this.reportTitleForm.value;

    if (!tmpInspectionReport.templateId || tmpInspectionReport.templateId === "" || tmpInspectionReport.templateId === undefined) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidInspectionRpt"));
    }

    if (!tmpInspectionReport.title) {
      throw new ApplicationError(this.translate.instant("inspectionReportEditPropertiesDialog.ErrInvalidInspectionRpt"));
    }

    const inspectionReportUpdate = this.inspectionReportService.convertInspectionReport2InpsctionReportUpdate(tmpInspectionReport);

    this.updateInspectionReport(inspectionReportUpdate).subscribe({
      next: (inspectionReportUpdate) => {
        this.inspectionReport = structuredClone({
          ...this.inspectionReport,
          ...inspectionReportUpdate
        });
      },
      error: (error) => {
        this.oscSpinnerService.hide(true);
        console.log(error);
        this.snackBar.open(this.translate.instant("inspectionReportEditPropertiesDialog.FailSave"));
      },
      complete: () => {
        this.data.inspectionReport = { ...this.inspectionReport };
        this.snackBar.open(this.translate.instant("inspectionReportEditPropertiesDialog.SuccessSave"));
        this.oscSpinnerService.hide(true);
        this.dialogRef.close(true);
      }
    });
  }

  updateInspectionReport(inspectionReportUpdate: InspectionReportUpdate): Observable<InspectionReport> {
    return this.inspectionReportService.updateInspectionReport(this.inspectionReport.reportId, inspectionReportUpdate);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
